import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const Terms: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-container">
      <div className="legal-content">
        <h1>Conditions Générales d'Utilisation (CGU)</h1>
        <p className="last-updated">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>

        <section>
          <h2>1. Objet</h2>
          <p>
            Les présentes Conditions Générales d'Utilisation (ci-après "CGU") régissent l'utilisation
            de l'application web <strong>Bob Map</strong>, éditée par Hugo Bachellier.
          </p>
          <p>
            En créant un compte sur Bob Map, vous acceptez sans réserve les présentes CGU.
          </p>
        </section>

        <section>
          <h2>2. Présentation du service</h2>
          <p>
            <strong>Bob Map</strong> est une application web permettant de :
          </p>
          <ul>
            <li>Partager votre localisation avec vos amis sur une carte interactive</li>
            <li>Voir la localisation de vos amis (selon leurs paramètres de visibilité)</li>
            <li>Gérer des groupes d'amis</li>
            <li>Ajouter des localisations temporaires (vacances, événements)</li>
            <li>Gérer finement la visibilité de votre adresse (5 niveaux de confidentialité)</li>
          </ul>
        </section>

        <section>
          <h2>3. Conditions d'accès</h2>

          <h3>3.1 Âge minimum</h3>
          <p>
            Vous devez avoir <strong>au moins 18 ans</strong> pour créer un compte Bob Map.
            Si vous êtes mineur, vous devez obtenir le consentement de vos parents ou tuteurs légaux.
          </p>

          <h3>3.2 Inscription</h3>
          <p>Pour utiliser Bob Map, vous devez :</p>
          <ul>
            <li>Fournir des informations exactes et à jour (nom, prénom, email, adresse)</li>
            <li>Créer un mot de passe sécurisé</li>
            <li>Accepter la <Link to="/privacy-policy">Politique de Confidentialité</Link></li>
            <li>Consentir au partage de votre localisation avec vos amis</li>
          </ul>

          <h3>3.3 Connexion via Google OAuth</h3>
          <p>
            Vous pouvez créer un compte via Google OAuth. Dans ce cas, Bob Map récupère votre nom,
            prénom, email et photo de profil depuis votre compte Google.
          </p>
        </section>

        <section>
          <h2>4. Obligations de l'utilisateur</h2>

          <h3>4.1 Utilisation conforme</h3>
          <p>Vous vous engagez à :</p>
          <ul>
            <li>Utiliser Bob Map de manière loyale et conforme à sa finalité</li>
            <li>Ne pas usurper l'identité d'une autre personne</li>
            <li>Ne pas diffuser de contenu illégal, diffamatoire ou offensant</li>
            <li>Ne pas tenter de contourner les mesures de sécurité</li>
            <li>Ne pas collecter les données d'autres utilisateurs de manière automatisée</li>
            <li>Respecter la vie privée de vos amis</li>
          </ul>

          <h3>4.2 Sécurité du compte</h3>
          <p>Vous êtes responsable de :</p>
          <ul>
            <li>La confidentialité de votre mot de passe</li>
            <li>Toutes les activités effectuées depuis votre compte</li>
            <li>La déconnexion de votre compte sur les appareils partagés</li>
          </ul>
          <p>
            En cas de suspicion de piratage, changez immédiatement votre mot de passe et
            contactez-nous à <a href="mailto:bobmap.contact@gmail.com">bobmap.contact@gmail.com</a>.
          </p>

          <h3>4.3 Exactitude des données de localisation</h3>
          <p>
            Vous vous engagez à fournir une adresse réelle et exacte. L'utilisation d'une fausse adresse
            peut entraîner la suspension de votre compte.
          </p>
        </section>

        <section>
          <h2>5. Propriété intellectuelle</h2>
          <p>
            Le code source, le design, les logos et tous les éléments de Bob Map sont la propriété
            exclusive de Hugo Bachellier.
          </p>
          <p>
            Toute reproduction, représentation, modification ou exploitation non autorisée est interdite
            et constitue une contrefaçon sanctionnée par le Code de la Propriété Intellectuelle.
          </p>
          <p>
            Les données cartographiques proviennent de <strong>OpenStreetMap</strong> (© OpenStreetMap contributors)
            et sont utilisées sous licence ODbL.
          </p>
        </section>

        <section>
          <h2>6. Données personnelles</h2>
          <p>
            Le traitement de vos données personnelles est décrit en détail dans notre{' '}
            <Link to="/privacy-policy">Politique de Confidentialité</Link>.
          </p>
          <p>
            En résumé :
          </p>
          <ul>
            <li>Vos données ne sont jamais vendues à des tiers</li>
            <li>Votre localisation n'est visible que par vos amis acceptés</li>
            <li>Vous contrôlez le niveau de précision de votre adresse (5 niveaux)</li>
            <li>Vous pouvez supprimer votre compte à tout moment</li>
          </ul>
        </section>

        <section>
          <h2>7. Responsabilités</h2>

          <h3>7.1 Responsabilité de l'éditeur</h3>
          <p>
            Bob Map est fourni "tel quel". Nous nous efforçons d'assurer la disponibilité et la sécurité
            du service, mais ne pouvons garantir :
          </p>
          <ul>
            <li>Une disponibilité 24h/24, 7j/7 sans interruption</li>
            <li>L'absence totale de bugs ou d'erreurs</li>
            <li>L'exactitude des données de géolocalisation (qui dépendent d'OpenStreetMap)</li>
          </ul>
          <p>
            Nous ne sommes pas responsables des dommages indirects résultant de l'utilisation de Bob Map
            (perte de données, préjudice commercial, etc.).
          </p>

          <h3>7.2 Responsabilité de l'utilisateur</h3>
          <p>
            Vous êtes seul responsable :
          </p>
          <ul>
            <li>De l'utilisation que vous faites du service</li>
            <li>Des informations que vous partagez avec vos amis</li>
            <li>De la gestion de vos paramètres de confidentialité</li>
          </ul>
        </section>

        <section>
          <h2>8. Suspension et résiliation</h2>

          <h3>8.1 Suspension par l'éditeur</h3>
          <p>
            Nous nous réservons le droit de suspendre ou supprimer votre compte sans préavis en cas de :
          </p>
          <ul>
            <li>Violation des présentes CGU</li>
            <li>Utilisation frauduleuse ou abusive du service</li>
            <li>Activité suspecte ou dangereuse</li>
            <li>Non-respect des droits d'autrui</li>
          </ul>

          <h3>8.2 Résiliation par l'utilisateur</h3>
          <p>
            Vous pouvez supprimer votre compte à tout moment depuis la page{' '}
            <Link to="/my-data">Mes Données</Link>. La suppression est <strong>immédiate et irréversible</strong>.
          </p>
        </section>

        <section>
          <h2>9. Modifications du service</h2>
          <p>
            Nous nous réservons le droit de modifier, suspendre ou interrompre tout ou partie de Bob Map
            à tout moment, sans préavis ni indemnité.
          </p>
          <p>
            Les modifications majeures seront notifiées par email aux utilisateurs actifs.
          </p>
        </section>

        <section>
          <h2>10. Modifications des CGU</h2>
          <p>
            Les présentes CGU peuvent être modifiées à tout moment. Les modifications prendront effet
            dès leur publication sur cette page.
          </p>
          <p>
            En continuant à utiliser Bob Map après modification, vous acceptez les nouvelles CGU.
          </p>
        </section>

        <section>
          <h2>11. Droit applicable et juridiction</h2>
          <p>
            Les présentes CGU sont régies par le droit français.
          </p>
          <p>
            En cas de litige, et après tentative de résolution amiable, les tribunaux de{' '}
            <strong>Tours (France)</strong> seront seuls compétents.
          </p>
        </section>

        <section>
          <h2>12. Médiation</h2>
          <p>
            Conformément à l'article L.612-1 du Code de la consommation, vous pouvez recourir
            gratuitement à un médiateur de la consommation en cas de litige :
          </p>
          <p>
            <strong>Médiation de la consommation</strong><br />
            Site web :{' '}
            <a href="https://www.economie.gouv.fr/mediation-conso" target="_blank" rel="noopener noreferrer">
              www.economie.gouv.fr/mediation-conso
            </a>
          </p>
        </section>

        <section>
          <h2>13. Contact</h2>
          <p>
            Pour toute question concernant les présentes CGU, contactez-nous :
          </p>
          <p>
            Email : <a href="mailto:bobmap.contact@gmail.com">bobmap.contact@gmail.com</a><br />
            Adresse : 9 Allée de la Fauvette, 37100 Tours, France
          </p>
        </section>

        <div className="legal-footer">
          <Link to="/privacy-policy" className="legal-link">Politique de Confidentialité</Link>
          <Link to="/legal" className="legal-link">Mentions Légales</Link>
          <Link to="/my-data" className="legal-link">Gérer mes données</Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
